import { linkifyNbfc } from "../NbfcLink";
import { useEffect, useState } from "react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import Seo from "../Seo";
import AprCalculator from "./AprCalculator";
import type { IconCard, LabelValue, SectionHeading, ServicePageData } from "./serviceData";
import shieldSmIcon from "../../assets/services/shield-sm.svg";
import checkIcon from "../../assets/services/check.svg";
import plusIcon from "../../assets/services/plus.svg";

const container = "mx-auto w-full max-w-[1470px] px-4 sm:px-6 lg:px-12";
const sectionY = "py-12 md:py-[72px]";
const eyebrowText = "text-[12px] font-bold uppercase tracking-[1px] text-[#048be1]";
const sectionTitle = "font-inter text-[28px] font-bold leading-[36px] md:text-[34px] md:leading-[42px] lg:text-[40px] lg:leading-[48px]";
const cardBase = "rounded-[20px] border border-[#e5e7eb] bg-white p-5 drop-shadow-[0px_8px_12px_rgba(0,0,0,0.03)] sm:p-6";
const iconBox = "flex size-12 shrink-0 items-center justify-center rounded-[12px] bg-[#edf2fd]";
const chip = "inline-flex rounded-full bg-[#edf2fd] px-3 py-2 text-[12px] font-semibold text-[#048be1]";

const Heading = ({ eyebrow, title, text, dark, eyebrowBadge }: SectionHeading & { dark?: boolean; eyebrowBadge?: boolean }) => (
  <div className="flex flex-col items-center gap-3 text-center">
    {eyebrowBadge ? (
      <span className={`rounded-[4px] bg-white p-2 ${eyebrowText}`}>{eyebrow}</span>
    ) : (
      <p className={eyebrowText}>{eyebrow}</p>
    )}
    <h2 className={`${sectionTitle} ${dark ? "text-white" : "text-black"}`}>{title}</h2>
    {text && (
      <p className={`max-w-[1022px] text-[15px] leading-[24px] md:text-[16px] ${dark ? "text-[#c6c6c6]" : "text-[#6b7280]"}`}>{text}</p>
    )}
  </div>
);

const FeatureCard = ({ icon, title, text, chip: chipLabel }: IconCard) => (
  <div className={`${cardBase} flex h-full flex-col items-start gap-4`}>
    <div className={iconBox}>
      <img src={icon} alt={`${title} icon`} />
    </div>
    <h3 className="font-inter text-[20px] font-bold leading-[28px] text-black sm:text-[24px] sm:leading-[32px]">{title}</h3>
    <p className="text-[14px] leading-[22px] text-[#6b7280]">{text}</p>
    {chipLabel && <span className={`${chip} mt-auto`}>{chipLabel}</span>}
  </div>
);

const CriteriaCard = ({ label, value }: LabelValue) => (
  <div className="flex flex-col gap-2 rounded-[16px] border border-[#e5e7eb] bg-white p-5 lg:min-h-[128px]">
    <p className={eyebrowText}>{label}</p>
    <p className="font-inter text-[18px] font-medium leading-[28px] text-black sm:text-[20px] lg:text-[24px] lg:leading-[32px]">{value}</p>
  </div>
);

const DocumentCard = ({ title, items }: { title: string; items: LabelValue[] }) => (
  <div className="flex flex-col gap-4 rounded-[20px] border border-[#e5e7eb] bg-white p-5 drop-shadow-[0px_8px_12px_rgba(0,0,0,0.03)] sm:p-6">
    <p className={eyebrowText}>{title}</p>
    <ul className="flex flex-col gap-3">
      {items.map((item) => (
        <li key={item.label} className="flex items-start gap-3">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-[12px] bg-[#edf2fd]">
            <img src={checkIcon} alt="Check mark" />
          </span>
          <p className="text-[14px] font-semibold leading-[22px] text-black">
            <span className="font-extrabold">{item.label}</span> {item.value}
          </p>
        </li>
      ))}
    </ul>
  </div>
);

const ServicePage = ({ data }: { data: ServicePageData }) => {
  const { hero, about, eligibility, documents, loanTypes, steps, trust, calculator, faq, banner } = data;
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const websiteLink = `${import.meta.env.VITE_WEBSITE_LINK}/?utm_source=${data.utmSource}&utm_medium=SEO`;

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setOpenFaq(0);
  }, [data.slug]);

  useEffect(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    return () => {
      document.head.removeChild(robots);
    };
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const loanTypesSection = (
    <section key="loan-types" className={sectionY}>
      <div className={`${container} flex flex-col gap-8`}>
        <Heading {...loanTypes} />
        <div className="mx-auto grid w-full max-w-[1024px] grid-cols-1 gap-6 md:grid-cols-2">
          {loanTypes.cards.map((card) => (
            <FeatureCard key={card.title} {...card} />
          ))}
        </div>
      </div>
    </section>
  );

  const stepsSection = (
    <section key="steps" className={sectionY}>
      <div className={`${container} flex flex-col gap-8`}>
        <Heading {...steps} />
        <div className="flex flex-wrap justify-center gap-6">
          {steps.items.map((step, index) => (
            <div
              key={step.title}
              className={`${cardBase} flex w-full flex-col items-start gap-4 sm:w-[calc(50%-12px)] lg:w-[360px]`}
            >
              <span className="flex size-10 items-center justify-center rounded-[20px] bg-[#edf2fd] text-[16px] font-bold text-[#048be1]">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3 className="font-inter text-[20px] font-bold leading-[28px] text-black sm:text-[24px] sm:leading-[32px]">{step.title}</h3>
              <p className="text-[14px] leading-[22px] text-[#6b7280]">{step.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-white to-[#e4f1fe] font-sans">
      <Seo title={data.metaTitle} description={data.metaDescription} path={`/${data.slug}`} />
      <Navbar />

      <section className="pt-[81px]">
        <div className={`${container} grid grid-cols-1 items-center gap-10 py-10 md:py-14 lg:grid-cols-[minmax(0,720fr)_minmax(0,600fr)] lg:gap-12 lg:py-[72px]`}>
          <div className="flex flex-col items-start gap-6">
            {hero.eyebrow && (
              <span className="rounded-full bg-[#edf2fd] px-3 py-2 text-[12px] font-bold uppercase tracking-[1px] text-[#048be1]">
                {hero.eyebrow}
              </span>
            )}
            <h1 className="font-inter text-[32px] font-bold leading-[42px] text-black sm:text-[40px] sm:leading-[52px] xl:text-[48px] xl:leading-[64px]">
              {hero.title}
            </h1>
            <p className="text-[16px] leading-[26px] text-[#6b7280] sm:text-[18px] sm:leading-[28px]">{hero.text}</p>
            <div className="flex flex-wrap gap-3">
              {hero.chips.map((item) => (
                <span key={item} className="rounded-full bg-[#edf2fd] px-[14px] py-[10px] text-[13px] font-semibold text-[#048be1]">
                  {item}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={() => scrollToSection(hero.secondaryCtaScrollsToAbout ? "what-is" : "eligibility")}
                className="rounded-[12px] border border-[#e5e7eb] bg-white px-6 py-[14px] text-[16px] font-bold text-[#048be1] transition-colors hover:border-[#048be1]"
              >
                {hero.secondaryCta}
              </button>
              <a
                href={websiteLink}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-[12px] bg-[#ffb200] px-6 py-[14px] text-[16px] font-bold text-black transition-colors hover:bg-[#f0a700]"
              >
                Apply Now
              </a>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-[16px] bg-[#edf2fd]">
                <img src={shieldSmIcon} alt="Secure data shield" />
              </span>
              <p className="text-[14px] leading-[22px] text-[#6b7280]">{hero.note}</p>
            </div>
          </div>
          <div className="relative h-[340px] overflow-hidden rounded-[24px] border border-[#e5e7eb] shadow-[0px_12px_32px_0px_rgba(0,0,0,0.04)] sm:h-[480px] lg:h-[658px]">
            <img
              src={hero.image}
              alt={hero.title}
              className="absolute inset-0 size-full object-cover"
              style={{ objectPosition: hero.imagePosition }}
            />
          </div>
        </div>
      </section>

      <section id="what-is" className={`scroll-mt-[81px] bg-[#0a0f1c] ${sectionY}`}>
        <div className={`${container} flex flex-col gap-8`}>
          <div className="flex flex-col items-center gap-3 text-center">
            <p className={eyebrowText}>{about.eyebrow}</p>
            <h2 className={`${sectionTitle} text-white`}>{about.title}</h2>
            <div className="flex max-w-[1022px] flex-col gap-6 text-[15px] leading-[24px] text-[#c6c6c6] md:text-[16px]">
              {about.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
          {about.cards && (
            <div className="mx-auto grid w-full max-w-[1024px] grid-cols-1 gap-6 md:grid-cols-2">
              {about.cards.map((card) => (
                <FeatureCard key={card.title} {...card} />
              ))}
            </div>
          )}
          {about.cta && (
            <div className="flex justify-center">
              <a
                href={websiteLink}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-[12px] border border-[#e5e7eb] bg-white px-6 py-[14px] text-[16px] font-bold text-[#048be1] transition-colors hover:bg-[#edf2fd]"
              >
                {about.cta}
              </a>
            </div>
          )}
        </div>
      </section>

      <section id="eligibility" className={`scroll-mt-[81px] ${sectionY}`}>
        <div className={`${container} flex flex-col gap-8`}>
          <Heading eyebrow={eligibility.eyebrow} title={eligibility.title} />
          <div className="mt-0 flex flex-col gap-6 rounded-[24px] border border-[#e5e7eb] bg-[#f7fafc] p-5 sm:p-8 lg:mt-6">
            <h3 className="font-inter text-[24px] font-bold leading-[32px] text-black sm:text-[32px] sm:leading-[40px]">For Salaried</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
              {eligibility.salaried.map((item, index) => (
                <CriteriaCard key={`${item.label}-${index}`} {...item} />
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-6 rounded-[24px] border border-[#e5e7eb] bg-[#f7fafc] p-5 sm:p-8">
            <h3 className="font-inter text-[24px] font-bold leading-[32px] text-black sm:text-[32px] sm:leading-[40px]">For Self-Employed</h3>
            <p className="text-[15px] leading-[24px] text-[#6b7280] md:text-[16px]">{eligibility.selfEmployedNote}</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
              {eligibility.selfEmployed.map((item, index) => (
                <CriteriaCard key={`${item.label}-${index}`} {...item} />
              ))}
            </div>
          </div>
          <div className="rounded-[20px] bg-[#edf2fd] p-5 sm:p-6">
            <p className="text-[14px] font-semibold leading-[22px] text-[#048be1]">{eligibility.note}</p>
          </div>
        </div>
      </section>

      <section className={`bg-[#0a0f1c] ${sectionY}`}>
        <div className={`${container} flex flex-col gap-8`}>
          <Heading eyebrow={documents.eyebrow} title={documents.title} text={documents.text} dark />
          <div className="mx-auto grid w-full max-w-[1300px] grid-cols-1 gap-6 md:grid-cols-2">
            <DocumentCard title="For Salaried" items={documents.salaried} />
            <DocumentCard title="For Self-Employed" items={documents.selfEmployed} />
          </div>
        </div>
      </section>

      {data.stepsBeforeLoanTypes ? [stepsSection, loanTypesSection] : [loanTypesSection, stepsSection]}

      <section className={`bg-[#0a0f1c] ${sectionY}`}>
        <div className={`${container} flex flex-col gap-8`}>
          <Heading {...trust} dark eyebrowBadge />
          <div className="mx-auto grid w-full max-w-[1278px] grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {trust.cards.map((card) => (
              <FeatureCard key={card.title} {...card} />
            ))}
          </div>
        </div>
      </section>

      <section className={sectionY}>
        <div className={`${container} flex flex-col gap-8 md:gap-10`}>
          <div className="flex flex-col items-center gap-3 text-center">
            <p className={eyebrowText}>{calculator.eyebrow}</p>
            <h2 className={`${sectionTitle} text-black`}>{calculator.title}</h2>
            <p className="text-[15px] leading-[24px] text-[#40657d] md:text-[16px]">{calculator.text}</p>
          </div>
          <AprCalculator key={data.slug} variant={calculator.variant} ctaHref={websiteLink} />
        </div>
      </section>

      <section className={sectionY}>
        <div className={`${container} flex flex-col gap-8`}>
          <Heading eyebrow={faq.eyebrow} title={faq.title} />
          <div className="mx-auto flex w-full max-w-[1384px] items-stretch gap-6">
            <div className="relative hidden w-[472px] shrink-0 overflow-hidden rounded-[24px] border border-[#e5e7eb] bg-[#ffcf00] shadow-[0px_12px_32px_0px_rgba(0,0,0,0.04)] lg:block">
              <img
                src={faq.image}
                alt={`${data.navLabel} frequently asked questions`}
                className="absolute inset-0 size-full object-cover"
                style={{ objectPosition: faq.imagePosition }}
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-4 rounded-[24px] border border-[#e5e7eb] bg-white p-4 drop-shadow-[0px_8px_12px_rgba(0,0,0,0.03)] sm:gap-6 sm:p-8">
              {faq.items.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div key={item.question} className="rounded-[16px] border border-[#e5e7eb] p-4 sm:p-5">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-4 text-left"
                    >
                      <span className="text-[16px] font-semibold leading-[24px] text-black sm:text-[18px] sm:leading-[26px]">{item.question}</span>
                      <img
                        src={plusIcon}
                        alt={isOpen ? "Collapse answer" : "Expand answer"}
                        className={`shrink-0 transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}
                      />
                    </button>
                    <div className={`grid transition-all duration-300 ${isOpen ? "mt-3 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <p className="overflow-hidden text-[14px] leading-[22px] text-[#6b7280]">{linkifyNbfc(item.answer)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {banner && (
        <section className={`${sectionY} pb-0 md:pb-0`}>
          <div className={container}>
            {banner.variant === "split" ? (
              <div className="flex flex-col gap-8 rounded-[24px] bg-[#0a0f1c] p-6 drop-shadow-[0px_25px_25px_rgba(0,0,0,0.25)] sm:p-10 lg:flex-row lg:items-center lg:gap-12 lg:rounded-[32px] lg:p-12">
                <div className="flex flex-col items-start gap-6 lg:w-[720px] lg:shrink-0">
                  <h2 className="font-inter text-[32px] font-bold leading-[40px] text-white sm:text-[44px] sm:leading-[52px] lg:text-[56px] lg:leading-[64px]">
                    {banner.title}
                  </h2>
                  <p className="text-[16px] leading-[26px] text-[#99a1af] sm:text-[18px] sm:leading-[28px]">{banner.text}</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <a
                      href={websiteLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-[12px] bg-[#048be1] px-6 py-[14px] text-[16px] font-bold text-white transition-colors hover:bg-[#0379c4]"
                    >
                      {banner.primaryCta}
                    </a>
                    {banner.secondaryCta && (
                      <button
                        type="button"
                        onClick={() => scrollToSection("eligibility")}
                        className="rounded-[12px] bg-white px-6 py-[14px] text-[16px] font-bold text-[#0a0f1c] transition-colors hover:bg-[#edf2fd]"
                      >
                        {banner.secondaryCta}
                      </button>
                    )}
                  </div>
                </div>
                {banner.badges && (
                  <div className="flex w-full flex-col gap-5 rounded-[24px] border border-[rgba(4,139,225,0.3)] bg-[rgba(255,255,255,0.03)] p-6 sm:p-8 lg:w-[360px]">
                    {banner.badges.map((badge) => (
                      <p key={badge} className={eyebrowText}>{badge}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-6 rounded-[24px] bg-[#0a0f1c] px-6 py-10 text-center drop-shadow-[0px_25px_25px_rgba(0,0,0,0.25)] sm:p-12 lg:rounded-[32px]">
                <h2 className="font-inter text-[30px] font-bold leading-[38px] text-white sm:text-[44px] sm:leading-[52px] lg:text-[56px] lg:leading-[64px]">
                  {banner.title}
                </h2>
                <p className="text-[16px] leading-[26px] text-[#99a1af] sm:text-[18px] sm:leading-[28px]">{banner.text}</p>
                <a
                  href={websiteLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-[12px] bg-[#048be1] px-6 py-[14px] text-[16px] font-bold text-white transition-colors hover:bg-[#0379c4]"
                >
                  {banner.primaryCta}
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ServicePage;
